import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, MapPin, Calendar, Plane, Users, AlertCircle, ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import Mandala from "@/components/Mandala";
import SignatureExperiences from "@/components/SignatureExperiences";
import ShareButton from "@/components/ShareButton";
import SEO from "@/components/SEO";

const SLUG_OFFERING = "saddle-and-soul-an-autumn-equinox-retreat-for-women";
const SLUG_PAGE = "saddle-and-soul";

const INCLUSIONS = [
  "Six days / five nights of retreat experience",
  "Five nights of mountain accommodations",
  "Daily access to a resident herd of horses",
  "Two guided two-hour trail rides through golden aspens",
  "Sacred equine connection & intuitive groundwork sessions",
  "Morning embodiment, breathwork, and meditation",
  "Evening fire circles & integration rituals",
  "Creative, sensory, and nature-based practices",
  "Cold-process soap making workshop with Witcher Mountain Apothecary",
  "Custom felt + wool hat bar experience",
  "Equinox stargazing with a professional telescope",
  "Professional retreat photoshoot",
  "Visit to Mount Princeton Hot Springs & Spa",
  "Sauna and hot tub sessions",
  "All meals (excluding 1 lunch in Buena Vista) prepared by a private chef",
  "Round-trip transportation from Colorado Springs Airport",
];

const ACCOMMODATIONS = [
  { name: "The Farmhouse",   desc: "2-bedroom cabin with full bath and kitchen.",                                                                  status: "Available", soldOut: false },
  { name: "The Glass House", desc: "1-bedroom cabin sharing a bath and kitchen with the Farmhouse.",                                              status: "Available", soldOut: false },
  { name: "The Herd Homes",  desc: "4 tiny homes — 1 queen private room and 1 shared room with 2 single beds, each with full bath and kitchen.", status: "Available", soldOut: false },
];

const NOT_INCLUDED = [
  "Flights",
  "Gratuities (recommended for transportation & trail guides)",
  "Lunch in Buena Vista while visiting Mount Princeton Hot Springs",
  "Spa services at Mount Princeton Hot Springs",
];

const FALLBACKS = {
  hero: "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/uvz6fcvb_IMG_8274.jpeg",
  "invitation-portrait": "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/yipigdji_Holly%202014%20Passions%20Production%20Co-40.jpeg",
  "rhythm-1": "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/rpeocub0_SEDONARETREAT-131.png",
  "copper-lodge": "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/uvz6fcvb_IMG_8274.jpeg",
  "rhythm-2": "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/f2nrwkzu_Page89option.jpg",
  nourishment: "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/f2nrwkzu_Page89option.jpg",
  holly: "https://customer-assets.emergentagent.com/job_retreat-hub-12/artifacts/nk1vssa4_SEDONARETREAT-52.jpg",
};

export default function SaddleAndSoul() {
  const [offering, setOffering] = useState(null);
  const [images, setImages] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [offRes, imgRes] = await Promise.all([
          api.get(`/offerings/${SLUG_OFFERING}`),
          api.get(`/page-images/${SLUG_PAGE}`),
        ]);
        setOffering(offRes.data);
        setImages(imgRes.data?.slots || {});
      } catch {}
    })();
  }, []);

  const img = (key) => images[key]?.[0] || FALLBACKS[key];
  const accomGallery = images["accommodations-gallery"] || [];

  return (
    <div className="bg-aviana-paper" data-testid="saddle-soul-page">
      <SEO
        title="Saddle & Soul · An Autumn Equinox Retreat for Women"
        description="A 6-day women's autumn equinox retreat in the Colorado Rockies — Sept 18-23, 2026. Horses, Mount Princeton hot springs, equinox stargazing with a pro telescope, sound healing, sister circles. Trio + sisterhood pricing. Limited to 12 sisters."
        path="/retreats/saddle-and-soul"
        image="/social/og-saddle-soul.jpg"
        keywords="autumn women's retreat Colorado, equinox retreat, horseback retreat for women, Mount Princeton hot springs, stargazing retreat, sister circle, fall yoga retreat 2026, equine connection retreat"
        type="event"
        jsonLd={offering ? {
          "@context": "https://schema.org",
          "@type": "Event",
          "name": "Saddle & Soul · An Autumn Equinox Retreat for Women",
          "description": "A 6-day women's autumn equinox retreat in the Colorado Rockies — horses, Mount Princeton hot springs, equinox stargazing, sound healing, sister circles.",
          "startDate": offering.start_date,
          "endDate": offering.end_date,
          "eventStatus": "https://schema.org/EventScheduled",
          "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
          "location": {
            "@type": "Place",
            "name": "The Copper Lodge · Buena Vista, Colorado",
            "address": {"@type": "PostalAddress", "addressLocality": "Buena Vista", "addressRegion": "CO", "addressCountry": "US"}
          },
          "image": offering.image_url,
          "organizer": {"@id": "https://avianayoga.com/#organization"},
          "performer": {"@id": "https://avianayoga.com/#holly"},
          "offers": {
            "@type": "Offer",
            "price": offering.price_usd,
            "priceCurrency": "USD",
            "url": "https://avianayoga.com/retreats/saddle-and-soul",
            "availability": offering.spots_left > 0 ? "https://schema.org/InStock" : "https://schema.org/SoldOut"
          }
        } : null}
      />
      {/* ─── Hero ─── */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-aviana-plumDeep via-aviana-plumDeep/90 to-aviana-ink">
          <img src={img("hero")} alt="" className="w-full h-full object-cover opacity-40 mix-blend-overlay" />
        </div>
        <Mandala size={500} variant="rose" className="absolute -top-24 -right-32 text-aviana-goldSoft/15 hidden md:block" />
        <div className="container-aviana relative z-10 pt-28 pb-20 text-aviana-cream">
          <p className="overline text-aviana-goldSoft mb-5" data-testid="hero-overline">An Autumn Equinox Retreat for Women</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.98] max-w-4xl">
            Saddle <span className="text-aviana-goldSoft italic">&</span><br /><em className="italic text-aviana-goldSoft">Soul</em>
          </h1>
          <div className="mt-9 flex flex-wrap gap-x-9 gap-y-3 text-aviana-cream/85 text-[14px]">
            <span className="flex items-center gap-2"><Calendar size={16} className="text-aviana-goldSoft" /> September 18 – 23, 2026</span>
            <span className="flex items-center gap-2"><MapPin size={16} className="text-aviana-goldSoft" /> Guffey, Colorado · The Copper Lodge</span>
            <span className="flex items-center gap-2"><Users size={16} className="text-aviana-goldSoft" /> 6 days · 5 nights · Small circle of women</span>
          </div>
          <p className="mt-8 text-aviana-cream/85 text-[18px] md:text-[20px] leading-[1.55] max-w-2xl font-serif italic">
            There comes a moment in the year when the light and the dark meet in perfect balance. A threshold. A turning inward. This is where we ride.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-5">
            <a href="#reserve" className="btn-gold" data-testid="hero-cta-reserve">Reserve your spot <ArrowRight size={14} /></a>
            <a href="#invitation" className="text-aviana-goldSoft hover:text-aviana-cream text-[13px] tracking-[0.18em] uppercase" data-testid="hero-cta-learn">
              ↓ Read the invitation
            </a>
            <ShareButton
              title="Saddle & Soul · An Autumn Equinox Retreat for Women"
              slug="saddle-and-soul"
              kind="retreat"
              label="Send to a sister"
            />
          </div>
          {typeof offering?.spots_left === "number" && offering.spots_left <= 5 && (
            <p className="mt-8 inline-flex items-center gap-2 bg-aviana-gold/15 border border-aviana-goldSoft/30 rounded-full px-4 py-2 text-aviana-goldSoft text-[12px] tracking-[0.14em] uppercase">
              <AlertCircle size={14} /> Only {offering.spots_left} {offering.spots_left === 1 ? "spot" : "spots"} left
            </p>
          )}
        </div>
      </section>

      {/* ─── The Deeper Invitation ─── */}
      <section className="py-28" id="invitation">
        <div className="container-aviana grid lg:grid-cols-12 gap-14 lg:gap-20 items-center max-w-6xl">
          <div className="lg:col-span-5 lg:order-2">
            <OvalImage src={img("invitation-portrait")} alt="The deeper invitation" aspect="3/4" />
          </div>
          <div className="lg:col-span-7 lg:order-1">
            <p className="overline-plum mb-4">The deeper invitation</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05] mb-8">
              A turning inward.<br /><em className="italic gold-text">A remembering.</em>
            </h2>
            <div className="space-y-5 text-aviana-inkSoft text-[16px] leading-[1.8]">
              <p>The Autumn Equinox is a sacred pause. A time to take stock of what has bloomed, what is ready to be released, and what truth is asking to be honored moving forward.</p>
              <p>Saddle &amp; Soul is an immersion into that moment. We ride <em>not to escape</em> life — <strong className="text-aviana-plum">but to return to ourselves.</strong></p>
              <p>Over the course of six days, you'll ride through breathtaking landscapes as the aspens turn golden, experience profound connection with horses as mirrors of your inner world, and be held in intentional, intimate circles of women walking a similar path.</p>
              <p className="font-serif italic text-aviana-plum text-[18px] leading-[1.5]">This is not about becoming someone new. It is about coming home to the woman you already are.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Cinematic band 1 ─── */}
      <CinematicBand src={img("rhythm-1")} caption="Ride through breathtaking landscapes as the aspens turn golden yellow." />

      {/* ─── A Day at the Copper Lodge ─── */}
      <section className="py-24 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden">
        <Mandala size={380} variant="lotus" className="absolute -bottom-12 -right-16 text-aviana-goldSoft/10 hidden md:block" />
        <div className="container-aviana relative grid lg:grid-cols-12 gap-12 lg:gap-16 items-center max-w-6xl">
          <div className="lg:col-span-5">
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] ring-1 ring-aviana-goldSoft/30 shadow-2xl">
              <img src={img("copper-lodge")} alt="The Copper Lodge" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-aviana-plumDeep/40 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="text-[10px] tracking-[0.22em] uppercase text-aviana-goldSoft">The Copper Lodge</p>
                <p className="font-display text-2xl text-aviana-cream mt-1">Guffey, Colorado</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="overline text-aviana-goldSoft mb-4">The experience</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-cream leading-[1.05]">A day balanced<br /><em className="italic text-aviana-goldSoft">in equinox.</em></h2>
            <div className="mt-8 space-y-5 text-aviana-cream/85 text-[15px] leading-[1.8]">
              <p>Each day is intentionally designed to balance movement and stillness, adventure and integration, connection and personal space.</p>
              <p>Mornings begin with embodiment, breathwork, and meditation — settling the nervous system before the aspens call you into the saddle. Afternoons unfold into guided rides, intuitive groundwork with the herd, or expansive hikes through canyon land.</p>
              <p>Evenings gather us around fire circles — stories, songs, integration rituals beneath the equinox sky. Creative, sensory, and nature-based practices weave throughout: somatic painting, scent-led meditations, quiet writing under blankets.</p>
              <p className="font-serif italic text-aviana-goldSoft text-[16px]">Deep rest. Real laughter. The kind of connection that ripples on for years.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Inclusions ─── */}
      <section className="py-24">
        <div className="container-aviana">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="overline-plum mb-3">What's woven in</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink">Everything held with <em className="italic gold-text">care.</em></h2>
          </div>
          <ul className="grid md:grid-cols-2 gap-x-12 gap-y-3 max-w-4xl mx-auto">
            {INCLUSIONS.map((line) => (
              <li key={line} className="flex gap-3 py-2 border-b border-aviana-border/60">
                <Check size={16} className="text-aviana-gold shrink-0 mt-1" />
                <span className="text-aviana-inkSoft text-[15px] leading-[1.6]">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ─── Signature Experiences — the soulful selling points ─── */}
      <SignatureExperiences variant="saddle" />

      {/* ─── Who This Is For ─── */}
      <section className="py-24 bg-aviana-cream/40">
        <div className="container-aviana max-w-3xl text-center">
          <p className="overline-plum mb-3">Who this is for</p>
          <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">This space is for <em className="italic gold-text">the woman who…</em></h2>
          <ul className="mt-10 space-y-4 text-aviana-inkSoft text-[16px] leading-[1.7] text-left max-w-xl mx-auto">
            {[
              "Has been holding it all together for everyone else",
              "Feels the quiet pull for something more… deeper… truer",
              "Craves connection — with herself, with nature, with other women",
              "Is ready to soften without losing her strength",
              "Knows she deserves to be held, too",
            ].map((line) => (
              <li key={line} className="flex gap-4">
                <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-aviana-gold flex-shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <p className="mt-12 font-serif italic text-aviana-plum text-[18px]">You won't leave the same.</p>
        </div>
      </section>

      {/* ─── Cinematic band 2 ─── */}
      <CinematicBand src={img("rhythm-2")} caption="Evening fire circles, integration rituals, real connection." />

      {/* ─── Nourishment ─── */}
      <section className="py-28">
        <div className="container-aviana grid lg:grid-cols-12 gap-14 lg:gap-20 items-center max-w-6xl">
          <div className="lg:col-span-5">
            <OvalImage src={img("nourishment")} alt="Chef-prepared meals" aspect="4/5" />
          </div>
          <div className="lg:col-span-7">
            <p className="overline-plum mb-3">The nourishment</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">Meals that <em className="italic gold-text">hold you.</em></h2>
            <div className="mt-7 space-y-5 text-aviana-inkSoft text-[15px] leading-[1.8]">
              <p>The cornerstone of every Aviana retreat — fresh, seasonal, thoughtfully prepared meals designed to energize your body and soothe your nervous system.</p>
              <p><strong className="text-aviana-plum">All meals (excluding one lunch in Buena Vista) are prepared by our private chef</strong>, using locally sourced and high-quality ingredients. Mornings include nourishing breakfast options that let you begin your day slowly and intuitively.</p>
              <p className="italic text-aviana-muted text-[13.5px]">Special dietary needs can be honored and accommodated with advance notice.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Accommodations ─── */}
      <section className="py-24 bg-aviana-cream/40">
        <div className="container-aviana">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="overline-plum mb-3">The accommodations</p>
            <h2 className="font-display text-4xl md:text-5xl text-aviana-ink">Mountain cabins, chosen with <em className="italic gold-text">care.</em></h2>
            <p className="mt-5 text-aviana-inkSoft leading-[1.75] text-[15px]">Comfortable, thoughtfully chosen mountain accommodations that support rest, privacy, and connection — free from light pollution and unnecessary noise.</p>
          </div>
          <AccommodationSlideshow photos={accomGallery} />
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mt-12">
            {ACCOMMODATIONS.map((a) => (
              <div key={a.name} className="bg-white rounded-2xl border border-aviana-border p-7">
                <h3 className="font-display text-2xl text-aviana-ink">{a.name}</h3>
                <p className="mt-2 text-aviana-inkSoft text-[14px] leading-[1.65]">{a.desc}</p>
                <p className={`mt-5 text-[11px] tracking-[0.18em] uppercase font-semibold ${a.soldOut ? "text-aviana-muted" : "text-green-700"}`}>{a.status}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Investment + Reserve (3-tier) ─── */}
      <section id="reserve" className="py-24 bg-aviana-plumDeep text-aviana-cream relative overflow-hidden">
        <Mandala size={520} variant="star" className="absolute -top-20 left-1/2 -translate-x-1/2 text-aviana-goldSoft/10" />
        <div className="container-aviana relative max-w-5xl text-center">
          <p className="overline text-aviana-goldSoft mb-3">The investment</p>
          <h2 className="font-display text-4xl md:text-5xl text-aviana-cream">Reserve your spot.</h2>
          <p className="mt-5 text-aviana-cream/70 max-w-2xl mx-auto leading-[1.7]">The deeper your circle, the deeper the savings — gather a sister or two and ride together at the autumn equinox.</p>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            <TierCard
              label="Solo"
              price="$3,575"
              sub="Private space · per person"
              tone="ink"
              cta="Reserve solo →"
              href={`/register-retreat/${SLUG_OFFERING}`}
              testid="reserve-solo-btn"
            />
            <TierCard
              label="Sisterhood Duo"
              price="$3,475"
              sub="Register with 1 friend · per person · save $100"
              tone="ink"
              cta="Reserve Duo →"
              href={`/register-retreat/${SLUG_OFFERING}?tier=sisterhood`}
              testid="reserve-duo-btn"
            />
            <TierCard
              label="Sisterhood Trio"
              price="$3,350"
              sub="Register with 2 friends · per person · save $225"
              tone="gold"
              cta="Reserve Trio →"
              href={`/register-retreat/${SLUG_OFFERING}?tier=trio`}
              featured
              testid="reserve-trio-btn"
            />
          </div>

          <div className="mt-10 bg-aviana-plumDeep/40 border border-aviana-gold/30 rounded-2xl p-8 text-left max-w-3xl mx-auto">
            <p className="font-display text-2xl text-aviana-cream mb-4">A <span className="text-aviana-goldSoft">$500 non-refundable deposit</span> secures your place.</p>
            <ul className="space-y-3 text-aviana-cream/85 text-[14px]">
              <li className="flex gap-3"><Check size={16} className="text-aviana-goldSoft shrink-0 mt-0.5" /> Remaining balance paid in full by <strong>September 3rd</strong>, or</li>
              <li className="flex gap-3"><Check size={16} className="text-aviana-goldSoft shrink-0 mt-0.5" /> Split into monthly payments leading up to the retreat</li>
              <li className="flex gap-3"><Check size={16} className="text-aviana-goldSoft shrink-0 mt-0.5" /> Pay by Stripe (card), Venmo, or Zelle at checkout</li>
            </ul>
          </div>
          {typeof offering?.spots_left === "number" && (
            <p className="mt-8 text-aviana-goldSoft text-[12px] tracking-[0.18em] uppercase">{offering.spots_left} {offering.spots_left === 1 ? "spot" : "spots"} remaining</p>
          )}
        </div>
      </section>

      {/* ─── Meet Holly ─── */}
      <section className="py-24">
        <div className="container-aviana grid lg:grid-cols-12 gap-12 lg:gap-16 items-center max-w-5xl">
          <div className="lg:col-span-5">
            <OvalImage src={img("holly")} alt="Holly Johnson" aspect="3/4" />
          </div>
          <div className="lg:col-span-7">
            <p className="overline-plum mb-3">Your facilitator</p>
            <h2 className="font-display text-4xl text-aviana-ink">Holly Johnson</h2>
            <p className="mt-1 text-aviana-gold italic">International yoga teacher trainer · author · retreat specialist</p>
            <div className="mt-6 space-y-4 text-aviana-inkSoft leading-[1.75]">
              <p>Holly blossoms from an extensive background of research, study, and direct experience of therapeutic avenues to balance and mend the relationships between mind, body, and Spirit.</p>
              <p>A former professional in the horse industry, she attributes much of her personal growth and spiritual understanding to the medicine of the horse. She is certified as a nutritional therapist, personal trainer, equine massage therapist, aromatherapist, Reiki master, Thai bodyworker, and 200-hour yoga instructor.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Getting There + Not Included ─── */}
      <section className="py-20 bg-aviana-cream/40">
        <div className="container-aviana grid md:grid-cols-2 gap-10 max-w-5xl">
          <div>
            <p className="overline-plum mb-3 flex items-center gap-2"><Plane size={14} /> Getting there</p>
            <h3 className="font-display text-2xl text-aviana-ink mb-4">The Copper Lodge · Guffey, CO</h3>
            <p className="text-aviana-inkSoft leading-[1.75] text-[15px]">10591 Co Rd 102, Guffey, CO 80820</p>
            <div className="mt-5 space-y-3 text-aviana-inkSoft leading-[1.75] text-[15px]">
              <p><strong>Group transport from Colorado Springs Airport:</strong> Shuttle leaves at <strong>2:30 pm on Friday September 18th</strong> — you must be available for pick-up at that time. We recommend booking flights that arrive by <strong>2:00 pm</strong>.</p>
              <p><strong>Denver International Airport:</strong> Rent a car, or arrange transport to Colorado Springs Airport in time for the 2:30 pm shuttle.</p>
              <p><strong>Returning home:</strong> Shuttle departs the Copper Lodge at <strong>11 am on September 23rd</strong>, arriving by 12:30 pm to Colorado Springs Airport. Book your return flight for <strong>no earlier than 1:45 pm</strong>.</p>
              <p className="text-[13px] text-aviana-muted italic mt-3 pt-3 border-t border-aviana-border/60"><strong className="not-italic text-aviana-ink">Missed the shuttle?</strong> An Uber/Lyft from Colorado Springs Airport to the Copper Lodge runs approximately <strong className="not-italic text-aviana-ink">$115 each way</strong>.</p>
            </div>
          </div>
          <div>
            <p className="overline-plum mb-3">Not included</p>
            <h3 className="font-display text-2xl text-aviana-ink mb-4">A few details to budget for.</h3>
            <ul className="space-y-2 text-aviana-inkSoft text-[15px]">
              {NOT_INCLUDED.map((l) => (
                <li key={l} className="flex gap-3"><span className="text-aviana-muted/40 mt-1">•</span> {l}</li>
              ))}
            </ul>
            <p className="mt-6 text-[12px] text-aviana-muted italic">Travel insurance is strongly recommended. TravelGuard offers comprehensive plans including <em>"Cancel for Any Reason"</em> coverage.</p>
          </div>
        </div>
      </section>

      {/* ─── Cancellation policy ─── */}
      <section className="py-20">
        <div className="container-aviana max-w-3xl">
          <p className="overline-plum mb-3">Cancellation policy</p>
          <h3 className="font-display text-2xl text-aviana-ink mb-6">Designed with care, anchored in reality.</h3>
          <div className="space-y-4 text-aviana-inkSoft text-[14px] leading-[1.75]">
            <p>We understand life can be unpredictable. We've designed this policy with care and compassion — and at the same time, hosting an intimate retreat requires significant advance planning and non-refundable expenses.</p>
            <div className="grid sm:grid-cols-3 gap-3 mt-6">
              <div className="bg-aviana-cream rounded-xl border border-aviana-border p-5">
                <p className="overline text-aviana-gold text-[10px] mb-2">60+ days before</p>
                <p className="text-[13px]">Refund of all payments beyond the $500 deposit. Travel costs non-refundable.</p>
              </div>
              <div className="bg-aviana-cream rounded-xl border border-aviana-border p-5">
                <p className="overline text-aviana-gold text-[10px] mb-2">30–59 days before</p>
                <p className="text-[13px]">50% credit toward another Aviana retreat within 12 months. Remaining balance forfeited.</p>
              </div>
              <div className="bg-aviana-cream rounded-xl border border-aviana-border p-5">
                <p className="overline text-aviana-gold text-[10px] mb-2">0–29 days before</p>
                <p className="text-[13px]">No refunds or credits. All balances paid in full by September 3rd.</p>
              </div>
            </div>
            <p className="mt-6 italic text-aviana-muted">Full cancellation policy and details on travel insurance available — reach out to <a href="mailto:avianayoga@gmail.com" className="text-aviana-plum hover:text-aviana-gold underline">avianayoga@gmail.com</a> with any questions.</p>
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-24 bg-aviana-ink text-aviana-cream text-center relative overflow-hidden">
        {images["closing-bg"]?.[0] && (
          <div className="absolute inset-0">
            <img src={img("closing-bg")} alt="" className="w-full h-full object-cover opacity-30 mix-blend-overlay" />
          </div>
        )}
        <div className="container-aviana max-w-2xl relative z-10">
          <Mandala size={80} variant="lotus" className="text-aviana-goldSoft opacity-60 mx-auto mb-7" />
          <h2 className="font-display text-4xl md:text-5xl">Come home to the woman you already are.</h2>
          <p className="mt-5 text-aviana-cream/80 leading-[1.75]">The aspens are turning. The horses are waiting. The equinox is calling.</p>
          <a href="#reserve" className="btn-gold mt-10" data-testid="final-cta">Reserve at the equinox <ArrowRight size={14} /></a>
        </div>
      </section>
    </div>
  );
}

/* ───── Tier card (Solo / Duo / Trio) ───── */
function TierCard({ label, price, sub, cta, href, featured, testid }) {
  return (
    <Link
      to={href}
      data-testid={testid}
      className={`block rounded-2xl p-8 text-left transition-all ${
        featured
          ? "bg-aviana-goldSoft/10 border-2 border-aviana-goldSoft shadow-2xl scale-[1.02] hover:scale-[1.04]"
          : "bg-aviana-plumDeep/60 border border-aviana-gold/30 hover:border-aviana-gold/60"
      }`}
    >
      {featured && (
        <p className="overline text-aviana-goldSoft text-[10px] mb-2">★ Best value</p>
      )}
      <p className={`overline text-[10px] mb-2 ${featured ? "text-aviana-goldSoft" : "text-aviana-goldSoft"}`}>{label}</p>
      <p className={`font-display text-5xl ${featured ? "text-aviana-goldSoft" : "text-aviana-cream"}`}>{price}</p>
      <p className="text-aviana-cream/70 text-[12px] mt-2 leading-[1.55]">{sub}</p>
      <p className={`mt-6 text-[11px] tracking-[0.22em] uppercase ${featured ? "text-aviana-goldSoft" : "text-aviana-cream/80"}`}>{cta}</p>
    </Link>
  );
}

/* ───── Oval image (matches homepage halo style) ───── */
function OvalImage({ src, alt, aspect = "3/4" }) {
  const aspectClass = aspect === "4/5" ? "aspect-[4/5]" : "aspect-[3/4]";
  return (
    <div className="relative">
      <div className="absolute -inset-4 rounded-[50%] bg-gradient-to-br from-aviana-goldSoft via-aviana-gold to-aviana-goldDeep opacity-25 blur-2xl" />
      <div className={`relative overflow-hidden ring-[3px] ring-aviana-gold/40 ${aspectClass}`} style={{ borderRadius: "50%" }}>
        <img src={src} alt={alt} className="w-full h-full object-cover" />
      </div>
      <Mandala size={64} variant="lotus" className="absolute -top-3 -right-3 text-aviana-gold/70" />
    </div>
  );
}

/* ───── Cinematic photo band ───── */
function CinematicBand({ src, caption }) {
  return (
    <section className="relative h-[58vh] md:h-[65vh] overflow-hidden">
      <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover scale-105" />
      <div className="absolute inset-0 bg-gradient-to-b from-aviana-ink/30 via-transparent to-aviana-ink/60" />
      {caption && (
        <div className="absolute bottom-10 left-0 right-0 px-6 text-center">
          <p className="font-serif italic text-aviana-cream text-[16px] md:text-[22px] leading-[1.4] max-w-2xl mx-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">{caption}</p>
        </div>
      )}
    </section>
  );
}

/* ───── Accommodations slideshow ───── */
function AccommodationSlideshow({ photos }) {
  const [idx, setIdx] = useState(0);
  const total = photos?.length || 0;

  if (total === 0) {
    return (
      <div className="max-w-4xl mx-auto bg-white border border-dashed border-aviana-border rounded-[2rem] aspect-[16/9] flex flex-col items-center justify-center text-center p-10">
        <ImageIcon size={28} className="text-aviana-plum/40 mb-3" />
        <p className="overline-plum mb-1">Photos coming soon</p>
        <p className="text-aviana-muted text-[13px] max-w-md">Holly is curating the cabin photos for Saddle &amp; Soul.</p>
      </div>
    );
  }
  const prev = () => setIdx((i) => (i - 1 + total) % total);
  const next = () => setIdx((i) => (i + 1) % total);
  return (
    <div className="max-w-5xl mx-auto">
      <div className="relative rounded-[2rem] overflow-hidden aspect-[16/9] bg-aviana-plum/10 shadow-xl ring-1 ring-aviana-border">
        <img src={photos[idx]} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-aviana-ink/40 via-transparent to-transparent pointer-events-none" />
        {total > 1 && (
          <>
            <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-aviana-cream/90 border border-aviana-border text-aviana-ink hover:bg-aviana-cream flex items-center justify-center"><ChevronLeft size={18} /></button>
            <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-aviana-cream/90 border border-aviana-border text-aviana-ink hover:bg-aviana-cream flex items-center justify-center"><ChevronRight size={18} /></button>
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-aviana-ink/40 backdrop-blur-sm rounded-full px-3 py-2">
              {photos.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-aviana-cream" : "w-1.5 bg-aviana-cream/50"}`} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
